import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { apiClient } from '../apiTools/apiClient';
import { allRoutes, config } from '../constants/config';
import { uploadMedia } from '../utils/mediaUploadUtils';

const {
  API_LITGROUP_PROTOTYPES,
  API_LITGROUP_PROTOTYPE_BOOKS,
  API_LITGROUP_PROTOTYPE_AUTHOR_IMAGE,
  API_LITGROUP_PROTOTYPE_STATUS,
  API_LITGROUPS,
} = { ...allRoutes };

// ============================================================
// Helper: build URL with uuid substitution
// ============================================================
const prototypeUrl = (uuid) => `${API_LITGROUP_PROTOTYPES}/${uuid}`;
const booksUrl = (uuid) => `${API_LITGROUP_PROTOTYPE_BOOKS}/${uuid}/books`;
const bookUrl = (uuid, bookUuid) => `${API_LITGROUP_PROTOTYPE_BOOKS}/${uuid}/books/${bookUuid}`;
const authorImageUrl = (uuid) => `${API_LITGROUP_PROTOTYPE_AUTHOR_IMAGE}/${uuid}/author-image`;
const statusUrl = (uuid) => `${API_LITGROUP_PROTOTYPE_STATUS}/${uuid}/status`;
const litGroupBooksUrl = (litGroupUuid) => `${API_LITGROUPS}/${litGroupUuid}/books`;
const litGroupBookUrl = (litGroupUuid, bookUuid) => `${API_LITGROUPS}/${litGroupUuid}/books/${bookUuid}`;
const litGroupProfileImageUrl = (litGroupUuid) => `${API_LITGROUPS}/${litGroupUuid}/profile-image`;

// ============================================================
// Async Thunks
// ============================================================

/**
 * Fetch all prototypes for the current user
 */
export const fetchPrototypes = createAsyncThunk(
  'litGroupPrototype/fetchPrototypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API_LITGROUP_PROTOTYPES);
      return response.data.prototypes || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Fetch a single prototype with its books
 */
export const fetchPrototypeDetail = createAsyncThunk(
  'litGroupPrototype/fetchPrototypeDetail',
  async (uuid, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(prototypeUrl(uuid));
      return {
        prototype: response.data.prototype,
        books: response.data.books || [],
        // Live LitGroup books (present only for a published prototype); the wizard
        // shows these instead of the draft PrototypeBooks once the group is live.
        liveBooks: response.data.liveBooks || []
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Create a new prototype
 */
export const createPrototype = createAsyncThunk(
  'litGroupPrototype/createPrototype',
  async (prototypeData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(API_LITGROUP_PROTOTYPES, prototypeData);
      return response.data.prototype;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Update prototype metadata
 */
export const updatePrototype = createAsyncThunk(
  'litGroupPrototype/updatePrototype',
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(prototypeUrl(uuid), data);
      return response.data.prototype;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Delete (archive) a prototype
 */
export const deletePrototype = createAsyncThunk(
  'litGroupPrototype/deletePrototype',
  async ({ uuid, confirmDelete = false }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(prototypeUrl(uuid), {}, {
        data: { confirmDelete },
        // Cascades through the prototype's books and assets — same reasoning as
        // deleteLiveLitGroup below. A 409 here is the DESIGNED path, not a failure: it means a
        // published Literary Group still reads its credits, and the caller turns that into the
        // offer to delete the group first. Declared so the console does not report it as an error.
        timeout: config.TIMEOUTS.CASCADE,
        expectedStatuses: [409],
      });
      return { uuid, message: response.data.message };
    } catch (error) {
      // Special handling for confirmation-required responses
      if (error.response?.status === 409 && error.response?.data?.requiresConfirmation) {
        return rejectWithValue({
          requiresConfirmation: true,
          message: error.response.data.error
        });
      }
      // A refusal because a published group still reads its credits. Carry the blocker through
      // rather than flattening it to a string — the caller can offer the way past it, which is to
      // delete that Literary Group.
      if (error.response?.status === 409 && error.response?.data?.blockedBy) {
        return rejectWithValue({
          blockedBy: error.response.data.blockedBy,
          message: error.response.data.error,
        });
      }
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Delete a PUBLISHED Literary Group — the group, its books, their characters, voices, rendered
 * audio and S3 files, and (on the way out) the prototype it was published from.
 *
 * The slice already had a deleteLitGroup, pointing at /api/delete-lit-group. No such route
 * exists; the real one is DELETE /api/delete-literary-group?uuid=. So there was no working path
 * from the UI to remove a Literary Group at all — which is why the prototype delete's "delete the
 * Literary Group first" was advice with nothing behind it.
 *
 * deleteType=hard IS LOAD-BEARING. That route reads
 * `req.query.deleteType ?? config.defaultDeleteType`, and for a LitGroup the default is SOFT —
 * which only sets role='Deleted'. The group, its books, their lines and its source prototype all
 * survive; it just stops appearing in lists. Without this parameter the prototype that sent the
 * operator here would still be blocked by a group that looks gone, and the dialog promising the
 * books and audio would be removed would be a lie.
 */
export const deleteLiveLitGroup = createAsyncThunk(
  'litGroupPrototype/deleteLiveLitGroup',
  async ({ litGroupUuid }, { rejectWithValue }) => {
    try {
      // TIMEOUT IS LOAD-BEARING, for the same reason deleteType is. This walks the whole group —
      // books, characters, samples, variations, rendered audio and every S3 object behind it — and
      // Chapel 13 took 195 seconds to remove 22,389 nodes. On the 30-second default the request
      // aborts, the SERVER CARRIES ON AND FINISHES, and the operator is told the delete failed.
      // That is worse than a slow delete: it invites a second attempt at work already done.
      const response = await apiClient.delete(
        allRoutes.DELETE_API_LITERARY_GROUP,
        {},
        {
          params: { uuid: litGroupUuid, deleteType: 'hard' },
          timeout: config.TIMEOUTS.CASCADE,
        }
      );
      return { litGroupUuid, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Add a book record to a prototype (after PDF is uploaded to S3)
 */
export const addPrototypeBook = createAsyncThunk(
  'litGroupPrototype/addPrototypeBook',
  async ({ prototypeUuid, fileName, fileSize, bookTitle, authorName, authorImageVersion, authorImageFileName }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(booksUrl(prototypeUuid), {
        fileName, fileSize, bookTitle,
        // Per-book author (name + pinned image version); authorImageFileName is sent only when
        // a NEW photo was uploaded, which tells the server to adopt/advance the canonical.
        authorName: authorName || null,
        authorImageVersion: authorImageVersion || null,
        authorImageFileName: authorImageFileName || null,
      });
      return { prototypeUuid, book: response.data.book };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * COMPOSITE: Upload PDF to S3, create book record, optionally upload cover + set coverFileName.
 * This is the main thunk called from the BookEntryForm "Save Book" action.
 *
 * Params: { prototypeUuid, bookTitle, pdfFile, pdfAlias, coverFile, coverAlias, userId }
 */
export const uploadAndAddBook = createAsyncThunk(
  'litGroupPrototype/uploadAndAddBook',
  async ({ prototypeUuid, bookTitle, pdfFile, pdfAlias, coverFile, coverAlias, userId,
           authorName, newAuthorImageFile, authorImageVersion, authorImageFileName, authorUuid }, { dispatch, rejectWithValue }) => {
    try {
      console.log('[uploadAndAddBook] Starting:', { prototypeUuid, bookTitle, pdfAlias, coverAlias });

      // --- Step 1: Upload PDF to S3 ---
      let pdfUrl = null;
      if (pdfFile) {
        console.log('[uploadAndAddBook] Uploading PDF to S3:', pdfAlias);
        pdfUrl = await uploadMedia(
          pdfFile,
          'litgroupPrototypeBook',
          { prototypeUuid, fileName: pdfAlias },
          userId
        );
        console.log('[uploadAndAddBook] PDF uploaded:', pdfUrl);
      }

      // --- Step 1b: Upload a NEW author photo for this book (optional) ---
      // Same stable filename, next version folder — mirrors uploadAndAddBookToLitGroup so the
      // draft flow establishes the per-book author image just like the series flow.
      let newAuthorImage = false;
      if (newAuthorImageFile && authorImageFileName) {
        await uploadMedia(
          newAuthorImageFile,
          'litgroupPrototypeAuthorImage',
          { prototypeUuid, authorUuid, version: String(authorImageVersion), fileName: authorImageFileName },
          userId
        );
        newAuthorImage = true;
        console.log('[uploadAndAddBook] New author image uploaded:', authorImageFileName, 'v' + authorImageVersion);
      }

      // --- Step 2: Create PrototypeBook record in DB ---
      const addResult = await dispatch(addPrototypeBook({
        prototypeUuid,
        fileName: pdfAlias,
        fileSize: pdfFile ? pdfFile.size : 0,
        bookTitle,
        authorName: authorName || null,
        authorImageVersion: authorImageVersion || null,
        authorImageFileName: newAuthorImage ? authorImageFileName : null,
      })).unwrap();

      const bookUuid = addResult.book.uuid;
      console.log('[uploadAndAddBook] Book record created:', bookUuid);

      // --- Step 3: Upload cover to S3 + update book record (if cover provided) ---
      let coverUrl = null;
      if (coverFile && coverAlias) {
        console.log('[uploadAndAddBook] Uploading cover to S3:', coverAlias);
        coverUrl = await uploadMedia(
          coverFile,
          'litgroupPrototypeBookCover',
          { prototypeUuid, fileName: coverAlias },
          userId
        );
        console.log('[uploadAndAddBook] Cover uploaded:', coverUrl);

        // Update the book record with the cover fileName
        await dispatch(updatePrototypeBook({
          prototypeUuid,
          bookUuid,
          data: { coverFileName: coverAlias },
        })).unwrap();
        console.log('[uploadAndAddBook] Book updated with coverFileName:', coverAlias);
      }

      return {
        bookUuid,
        bookTitle,
        pdfAlias,
        coverAlias: coverAlias || null,
        pdfUrl,
        coverUrl,
      };
    } catch (error) {
      console.error('[uploadAndAddBook] Error:', error);
      return rejectWithValue(error?.message || error?.toString() || 'Upload failed');
    }
  }
);

/**
 * COMPOSITE: Add a book to an ALREADY-PUBLISHED LitGroup (series grows over time).
 *
 * Mirrors `uploadAndAddBook`, but instead of registering a draft PrototypeBook it
 * uploads to the SAME prototype-keyed S3 path (the LitGroup's source prototype,
 * which is `activePrototype.uuid` in the wizard) and then hits
 * `POST /api/litgroups/:litGroupUuid/books`, which imports the book into the live
 * LitGroup immediately — same-named characters dedupe against the existing cast,
 * so voices are reused automatically.
 *
 * Params: { litGroupUuid, prototypeUuid, bookTitle, pdfFile, pdfAlias, coverFile, coverAlias, userId }
 */
export const uploadAndAddBookToLitGroup = createAsyncThunk(
  'litGroupPrototype/uploadAndAddBookToLitGroup',
  async ({ litGroupUuid, prototypeUuid, bookTitle, pdfFile, pdfAlias, coverFile, coverAlias, userId, authorName, authorImageVersion, newAuthorImageFile, authorImageFileName, authorUuid }, { getState, rejectWithValue }) => {
    try {
      console.log('[uploadAndAddBookToLitGroup] Starting:', { litGroupUuid, prototypeUuid, bookTitle, pdfAlias, coverAlias, authorImageVersion });

      // --- Step 1: Upload PDF to the source prototype's S3 path ---
      // (copyBookFiles on the server reads from litgroupPrototypeBook/{userId, prototypeUuid, fileName})
      if (pdfFile) {
        await uploadMedia(
          pdfFile,
          'litgroupPrototypeBook',
          { prototypeUuid, fileName: pdfAlias },
          userId
        );
        console.log('[uploadAndAddBookToLitGroup] PDF uploaded:', pdfAlias);
      }

      // --- Step 2: Upload cover to the same prototype path (optional) ---
      if (coverFile && coverAlias) {
        await uploadMedia(
          coverFile,
          'litgroupPrototypeBookCover',
          { prototypeUuid, fileName: coverAlias },
          userId
        );
        console.log('[uploadAndAddBookToLitGroup] Cover uploaded:', coverAlias);
      }

      // --- Step 2b: Upload a NEW author photo for this book (optional) ---
      // Same stable filename, next version folder — so older books keep their pinned
      // version's image and this book displays the new one.
      let newAuthorImage = false;
      if (newAuthorImageFile && authorImageFileName) {
        await uploadMedia(
          newAuthorImageFile,
          'litgroupPrototypeAuthorImage',
          { prototypeUuid, authorUuid, version: String(authorImageVersion), fileName: authorImageFileName },
          userId
        );
        newAuthorImage = true;
        console.log('[uploadAndAddBookToLitGroup] New author image uploaded:', authorImageFileName, 'v' + authorImageVersion);
      }

      // --- Step 3: Import the book into the live LitGroup (runs the per-book pipeline) ---
      // The server runs a long pipeline (PDF processing, character import, opportunities)
      // before responding, and streams SSE `update` events as it goes. A fixed timeout
      // can't fit a full book, so use an IDLE (sliding) timer instead: abort only if the
      // SSE stream goes silent for IDLE_MS — the worst case becomes one silent processing
      // section, not the whole book. A hard MAX_MS backstop guards against a stuck loop
      // that keeps emitting events forever.
      const IDLE_MS = 120000;    // 2 min with no SSE activity = stalled
      const MAX_MS = 1800000;    // 30 min absolute backstop
      const controller = new AbortController();
      const started = Date.now();
      let lastTs = getState()?.user?.sse?.recentMessage?.timestamp || null;
      let lastActivity = Date.now();
      const watchdog = setInterval(() => {
        const ts = getState()?.user?.sse?.recentMessage?.timestamp || null;
        if (ts && ts !== lastTs) { lastTs = ts; lastActivity = Date.now(); } // SSE tick → reset
        if (Date.now() - lastActivity > IDLE_MS || Date.now() - started > MAX_MS) {
          controller.abort();
          clearInterval(watchdog);
        }
      }, 5000);

      let response;
      try {
        response = await apiClient.post(litGroupBooksUrl(litGroupUuid), {
          fileName: pdfAlias,
          bookTitle,
          coverFileName: coverAlias || null,
          // Per-book author (name + image).
          authorName: authorName || null,
          authorImageVersion: authorImageVersion || null,
          authorImageFileName: newAuthorImage ? authorImageFileName : null,
          newAuthorImage,
        }, {}, { signal: controller.signal, timeout: 0 }); // idle timer governs, not a fixed ceiling
      } finally {
        clearInterval(watchdog);
      }

      console.log('[uploadAndAddBookToLitGroup] Imported into LitGroup:', response.data);
      return { litGroupUuid, ...response.data };
    } catch (error) {
      console.error('[uploadAndAddBookToLitGroup] Error:', error);
      const stalled = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
      return rejectWithValue(
        stalled
          ? 'Import stalled — no progress for over 2 minutes. It may still finish on the server; refresh in a moment to check.'
          : (error.response?.data?.error || error?.message || 'Failed to add book to LitGroup')
      );
    }
  }
);

/**
 * Lightweight in-place edit of a live book's metadata: title / author name+photo / cover.
 * Uploads any new cover (to the live litGroup book-cover path) and any new author photo
 * (versioned, same as add), then PUTs the metadata. No PDF, no re-pipeline.
 */
export const uploadAndEditLitGroupBook = createAsyncThunk(
  'litGroupPrototype/uploadAndEditLitGroupBook',
  async ({ litGroupUuid, prototypeUuid, bookUuid, bookTitle, authorName, coverFile, coverAlias, newAuthorFile, authorImageVersion, authorImageFileName, authorUuid, userId, credits, audioCoverFile, audioCoverAlias }, { rejectWithValue }) => {
    try {
      let coverUrl = null;
      if (coverFile && coverAlias) {
        coverUrl = await uploadMedia(
          coverFile,
          'litGroupBookCover',
          { litGroupId: litGroupUuid, bookId: bookUuid, fileName: coverAlias },
          userId
        );
      }
      // The square ACX cover rides the SAME PathDef as the print cover, distinguished only
      // by filename. A separate path definition would have meant a registry change for what
      // is simply a second image in the same folder.
      let audioCoverUrl = null;
      if (audioCoverFile && audioCoverAlias) {
        audioCoverUrl = await uploadMedia(
          audioCoverFile,
          'litGroupBookCover',
          { litGroupId: litGroupUuid, bookId: bookUuid, fileName: audioCoverAlias },
          userId
        );
      }
      let newAuthorImage = false;
      if (newAuthorFile && authorImageFileName) {
        await uploadMedia(
          newAuthorFile,
          'litgroupPrototypeAuthorImage',
          { prototypeUuid, authorUuid, version: String(authorImageVersion), fileName: authorImageFileName },
          userId
        );
        newAuthorImage = true;
      }
      const response = await apiClient.put(litGroupBookUrl(litGroupUuid, bookUuid), {
        bookTitle: bookTitle || null,
        authorName: authorName || null,
        coverUrl,
        authorImageVersion: authorImageVersion || null,
        authorImageFileName: newAuthorImage ? authorImageFileName : null,
        newAuthorImage,
        // Audiobook credit fields, spread only when the form supplied them. The server reads
        // an ABSENT field as "leave alone" and an EMPTY one as "clear" — the distinction that
        // lets a BC delete their custom wording and get the generated credit back — so these
        // must not be sent as nulls when the form never touched them.
        ...(credits || {}),
        // The square cover's URL and filename, only when one was actually uploaded — an
        // absent field means "leave alone" server-side, so a save that did not touch the
        // cover must not send nulls over the stored one.
        ...(audioCoverUrl ? { audioCoverUrl, audioCoverFileName: audioCoverAlias } : {}),
      });
      return { litGroupUuid, bookUuid, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error?.message || 'Failed to edit book');
    }
  }
);

/**
 * Upload + set the LitGroup's OWN profile image (distinct from the author image).
 * Uploads via the literaryGroupProfileImage pathref (keyed by the live litGroupUuid),
 * then stores the URL on the LiteraryGroupProfile — the Book Worksheet's location.
 */
export const uploadAndSetLitGroupImage = createAsyncThunk(
  'litGroupPrototype/uploadAndSetLitGroupImage',
  async ({ litGroupUuid, imageFile, userId }, { rejectWithValue }) => {
    try {
      const url = await uploadMedia(
        imageFile,
        'literaryGroupProfileImage',
        { litGroupId: litGroupUuid },
        userId
      );
      const response = await apiClient.put(litGroupProfileImageUrl(litGroupUuid), { profileImageUrl: url });
      return { litGroupUuid, profileImageUrl: response.data?.profileImageUrl || url };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error?.message || 'Failed to set LitGroup image');
    }
  }
);

/**
 * Archive (soft-delete) a book in a published LitGroup. Sets isActive=false on the
 * server; the shared cast, voices, and opportunities are preserved. Reversible.
 */
/**
 * Publish ONE prototype into a live Literary Group.
 *
 * The wizard already knows which prototype it is on, and the server already publishes exactly
 * one — it stages the LitGroup and Book nodes, returns 202, and runs the per-book import in
 * the background, reporting over SSE.
 *
 * WHY THIS EXISTS RATHER THAN THE BATCH PAGE. Publishing used to route through
 * /publish-prototypes, a queue built to publish MANY Literary Groups at once — a thing this
 * product does not do. Everything that went wrong came from the detour and not from
 * publishing: the page had to LIST prototypes, so it showed every ready draft ever
 * abandoned; it had a QUEUE, so it needed a Cancel it could not honour; and it was a separate
 * PAGE, so leaving it dropped the operator out of the wizard entirely.
 *
 * None of that is fixed here. It is simply not built.
 */
/**
 * Stage a Literary Group's profile image while the group is still a DRAFT.
 *
 * Sibling of uploadAndSetLitGroupImage, which needs a live litGroupId and so cannot run
 * before publishing — the one moment an operator is thinking about the group's identity is
 * the one moment they could not give it a picture.
 *
 * Uploads to the prototype (litgroupPrototypeImage) and records only the FILENAME on the
 * node. Publish rebuilds both keys from the registry and copies it across, exactly as it
 * already does for the author image.
 */
export const uploadAndSetPrototypeImage = createAsyncThunk(
  'litGroupPrototype/uploadAndSetPrototypeImage',
  async ({ prototypeUuid, imageFile, userId }, { dispatch, rejectWithValue }) => {
    try {
      const fileName = imageFile?.name || 'group-image.jpg';
      await uploadMedia(imageFile, 'litgroupPrototypeImage', { prototypeUuid, fileName }, userId);
      // Store the filename so publish knows there is one to copy.
      await dispatch(updatePrototype({ uuid: prototypeUuid, data: { groupImageFileName: fileName } })).unwrap();
      return { prototypeUuid, groupImageFileName: fileName };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error?.message || 'Failed to stage group image');
    }
  }
);

export const publishPrototype = createAsyncThunk(
  'litGroupPrototype/publishPrototype',
  async ({ prototypeUuid }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `${API_LITGROUP_PROTOTYPES}/${prototypeUuid}/publish`,
        {},
        {},
        // The staging call is near-instant; the import that follows is not, and is awaited
        // over SSE rather than held open here.
        { timeout: 120000 }
      );
      return { prototypeUuid, ...(response.data || {}) };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const archiveLitGroupBook = createAsyncThunk(
  'litGroupPrototype/archiveLitGroupBook',
  async ({ litGroupUuid, bookUuid }, { rejectWithValue }) => {
    try {
      await apiClient.delete(litGroupBookUrl(litGroupUuid, bookUuid));
      return { litGroupUuid, bookUuid };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error?.message || 'Failed to archive book');
    }
  }
);

/**
 * COMPOSITE: Upload author image to S3, then update the prototype record.
 *
 * Params: { prototypeUuid, authorImageFile, authorImageAlias, userId, incrementVersion }
 */
export const uploadAndSetAuthorImage = createAsyncThunk(
  'litGroupPrototype/uploadAndSetAuthorImage',
  async ({ prototypeUuid, authorImageFile, authorImageAlias, userId, incrementVersion = false }, { dispatch, getState, rejectWithValue }) => {
    try {
      console.log('[uploadAndSetAuthorImage] Starting:', { prototypeUuid, authorImageAlias });

      const state = getState();
      const prototype = state.litGroupPrototype.activePrototype;
      const authorUuid = prototype?.authorUuid || 'unknown';
      // When incrementing version, upload to the NEW version folder so S3 path matches DB
      // Note: authorImageVersion may arrive as a Neo4j Integer object; force to plain number
      const rawVer = prototype?.authorImageVersion;
      const currentVersion = (rawVer && typeof rawVer === 'object' && rawVer.low !== undefined)
        ? rawVer.low          // Neo4j Integer → use .low (safe for values < 2^31)
        : Number(rawVer) || 1;
      const targetVersion = incrementVersion ? currentVersion + 1 : currentVersion;
      const version = String(targetVersion);

      // --- Step 1: Upload to S3 ---
      const imageUrl = await uploadMedia(
        authorImageFile,
        'litgroupPrototypeAuthorImage',
        { prototypeUuid, authorUuid, version, fileName: authorImageAlias },
        userId
      );
      console.log('[uploadAndSetAuthorImage] Image uploaded:', imageUrl);

      // --- Step 2: Update prototype record ---
      const result = await dispatch(updateAuthorImage({
        uuid: prototypeUuid,
        authorImageFileName: authorImageAlias,
        incrementVersion,
      })).unwrap();

      console.log('[uploadAndSetAuthorImage] Prototype updated:', result);
      return { imageUrl, ...result };
    } catch (error) {
      console.error('[uploadAndSetAuthorImage] Error:', error);
      return rejectWithValue(error?.message || error?.toString() || 'Author image upload failed');
    }
  }
);

/**
 * Update a prototype book (set coverFileName, authorImageVersion override)
 */
export const updatePrototypeBook = createAsyncThunk(
  'litGroupPrototype/updatePrototypeBook',
  async ({ prototypeUuid, bookUuid, data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(bookUrl(prototypeUuid, bookUuid), data);
      return { prototypeUuid, book: response.data.book };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Remove a book from a prototype
 */
export const removePrototypeBook = createAsyncThunk(
  'litGroupPrototype/removePrototypeBook',
  async ({ prototypeUuid, bookUuid }, { rejectWithValue }) => {
    try {
      await apiClient.delete(bookUrl(prototypeUuid, bookUuid));
      return { prototypeUuid, bookUuid };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Update author image (set fileName, optionally increment version)
 */
export const updateAuthorImage = createAsyncThunk(
  'litGroupPrototype/updateAuthorImage',
  async ({ uuid, authorImageFileName, incrementVersion = false }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(authorImageUrl(uuid), {
        authorImageFileName,
        incrementVersion
      });
      return {
        prototype: response.data.prototype,
        version: response.data.version
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Update prototype status (with transition validation)
 */
export const updatePrototypeStatus = createAsyncThunk(
  'litGroupPrototype/updatePrototypeStatus',
  async ({ uuid, status }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(statusUrl(uuid), { status });
      return {
        prototype: response.data.prototype,
        previousStatus: response.data.previousStatus
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Lock prototype (convenience: sets status to 'complete')
 */
export const lockPrototype = createAsyncThunk(
  'litGroupPrototype/lockPrototype',
  async (uuid, { dispatch, rejectWithValue }) => {
    try {
      const result = await dispatch(updatePrototypeStatus({ uuid, status: 'complete' })).unwrap();
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// ============================================================
// Initial State
// ============================================================
const initialState = {
  prototypes: [],
  activePrototype: null,
  activePrototypeBooks: [],
  activeLiveBooks: [], // live LitGroup :Book list for a published prototype (the active series)
  // { percent, step, message } while a LitGroup delete cascade is running, else null.
  // Slice-level on purpose — see setDeleteProgress.
  deleteProgress: null,
  loading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
    bookAdd: false,
    bookUpdate: false,
    bookRemove: false,
    litGroupBookAdd: false,
    litGroupBookArchive: false,
    litGroupBookEdit: false,
    litGroupImage: false,
    authorImage: false,
    status: false,
  },
  error: null,
  wizardStep: 1,
};

// ============================================================
// Slice
// ============================================================
const litGroupPrototypeSlice = createSlice({
  name: 'litGroupPrototype',
  initialState,
  reducers: {
    // Set wizard step (1-4)
    setWizardStep(state, action) {
      state.wizardStep = action.payload;
    },

    // Deletion progress, held HERE rather than in the wizard's own state.
    //
    // The wizard is mounted with key={activePrototype?.uuid}, deliberately, so switching Literary
    // Groups gives a fresh instance with no stale local state. That also means any change to the
    // active prototype unmounts it mid-delete and takes its progress with it: the bar vanished at
    // 5% and only reappeared when the next SSE tick rebuilt it, ten or twenty seconds later.
    //
    // A delete already outlives its own request — that is why the server sends DELETE_COMPLETE.
    // It should outlive a component remount too.
    setDeleteProgress(state, action) {
      state.deleteProgress = action.payload;
    },

    clearDeleteProgress(state) {
      state.deleteProgress = null;
    },

    // Clear active prototype (deselect)
    clearActivePrototype(state) {
      state.activePrototype = null;
      state.activePrototypeBooks = [];
      state.activeLiveBooks = [];
      state.wizardStep = 1;
    },

    // Clear error
    clearError(state) {
      state.error = null;
    },

    // Reset entire slice
    resetPrototypeState() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    // ==============================
    // FETCH PROTOTYPES (list)
    // ==============================
    builder
      .addCase(fetchPrototypes.pending, (state) => {
        state.loading.list = true;
        state.error = null;
      })
      .addCase(fetchPrototypes.fulfilled, (state, action) => {
        state.loading.list = false;
        state.prototypes = action.payload;
      })
      .addCase(fetchPrototypes.rejected, (state, action) => {
        state.loading.list = false;
        state.error = action.payload;
      })

      // ==============================
      // FETCH PROTOTYPE DETAIL
      // ==============================
      .addCase(fetchPrototypeDetail.pending, (state) => {
        state.loading.detail = true;
        state.error = null;
      })
      .addCase(fetchPrototypeDetail.fulfilled, (state, action) => {
        state.loading.detail = false;
        state.activePrototype = action.payload.prototype;
        state.activePrototypeBooks = action.payload.books;
        state.activeLiveBooks = action.payload.liveBooks || [];
      })
      .addCase(fetchPrototypeDetail.rejected, (state, action) => {
        state.loading.detail = false;
        state.error = action.payload;
      })

      // ==============================
      // CREATE PROTOTYPE
      // ==============================
      .addCase(createPrototype.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createPrototype.fulfilled, (state, action) => {
        state.loading.create = false;
        state.prototypes.unshift(action.payload);
        state.activePrototype = action.payload;
        state.activePrototypeBooks = [];
        state.activeLiveBooks = [];
        state.wizardStep = 1;
      })
      .addCase(createPrototype.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload;
      })

      // ==============================
      // UPDATE PROTOTYPE
      // ==============================
      .addCase(updatePrototype.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updatePrototype.fulfilled, (state, action) => {
        state.loading.update = false;
        const updated = action.payload;
        // Update in list
        const idx = state.prototypes.findIndex(p => p.uuid === updated.uuid);
        if (idx !== -1) state.prototypes[idx] = { ...state.prototypes[idx], ...updated };
        // Update active
        if (state.activePrototype?.uuid === updated.uuid) {
          state.activePrototype = { ...state.activePrototype, ...updated };
        }
      })
      .addCase(updatePrototype.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload;
      })

      // ==============================
      // DELETE PROTOTYPE
      // ==============================
      .addCase(deletePrototype.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(deletePrototype.fulfilled, (state, action) => {
        state.loading.delete = false;
        const { uuid } = action.payload;
        state.prototypes = state.prototypes.filter(p => p.uuid !== uuid);
        if (state.activePrototype?.uuid === uuid) {
          state.activePrototype = null;
          state.activePrototypeBooks = [];
          state.activeLiveBooks = [];
          state.wizardStep = 1;
        }
      })
      .addCase(deletePrototype.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload;
      })

      // ==============================
      // ADD PROTOTYPE BOOK
      // ==============================
      .addCase(addPrototypeBook.pending, (state) => {
        state.loading.bookAdd = true;
        state.error = null;
      })
      .addCase(addPrototypeBook.fulfilled, (state, action) => {
        state.loading.bookAdd = false;
        const { book } = action.payload;
        state.activePrototypeBooks.push(book);
        // Update bookCount in list
        const idx = state.prototypes.findIndex(p => p.uuid === action.payload.prototypeUuid);
        if (idx !== -1 && state.prototypes[idx].bookCount !== undefined) {
          state.prototypes[idx].bookCount += 1;
        }
      })
      .addCase(addPrototypeBook.rejected, (state, action) => {
        state.loading.bookAdd = false;
        state.error = action.payload;
      })

      // ==============================
      // ADD BOOK TO PUBLISHED LITGROUP (series grows over time)
      // ==============================
      // Note: the imported book becomes a LitGroup :Book, NOT a draft PrototypeBook,
      // so activePrototypeBooks (the prototype's own book list) is intentionally not
      // mutated here. Success is surfaced via a message in the wizard.
      .addCase(uploadAndAddBookToLitGroup.pending, (state) => {
        state.loading.litGroupBookAdd = true;
        state.error = null;
      })
      .addCase(uploadAndAddBookToLitGroup.fulfilled, (state, action) => {
        state.loading.litGroupBookAdd = false;
        // Optimistically bump the matching card's book count (fetchPrototypes also
        // refreshes it, but this guarantees the count moves immediately).
        const lgUuid = action.payload?.litGroupUuid;
        if (lgUuid) {
          const idx = state.prototypes.findIndex(p => p.publishedLitGroupUuid === lgUuid);
          if (idx !== -1 && typeof state.prototypes[idx].bookCount === 'number') {
            state.prototypes[idx].bookCount += 1;
          }
        }
      })
      .addCase(uploadAndAddBookToLitGroup.rejected, (state, action) => {
        state.loading.litGroupBookAdd = false;
        state.error = action.payload;
      })

      // ==============================
      // ARCHIVE (SOFT-DELETE) A LITGROUP BOOK
      // ==============================
      .addCase(archiveLitGroupBook.pending, (state) => {
        state.loading.litGroupBookArchive = true;
        state.error = null;
      })
      .addCase(archiveLitGroupBook.fulfilled, (state, action) => {
        state.loading.litGroupBookArchive = false;
        // Drop the archived book from the live series list immediately.
        state.activeLiveBooks = state.activeLiveBooks.filter(b => b.uuid !== action.payload.bookUuid);
        // Optimistically decrement the matching card's book count.
        const lgUuid = action.payload?.litGroupUuid;
        if (lgUuid) {
          const idx = state.prototypes.findIndex(p => p.publishedLitGroupUuid === lgUuid);
          if (idx !== -1 && typeof state.prototypes[idx].bookCount === 'number') {
            state.prototypes[idx].bookCount = Math.max(0, state.prototypes[idx].bookCount - 1);
          }
        }
      })
      .addCase(archiveLitGroupBook.rejected, (state, action) => {
        state.loading.litGroupBookArchive = false;
        state.error = action.payload;
      })

      // ==============================
      // EDIT A LITGROUP BOOK (metadata)
      // ==============================
      .addCase(uploadAndEditLitGroupBook.pending, (state) => {
        state.loading.litGroupBookEdit = true;
        state.error = null;
      })
      .addCase(uploadAndEditLitGroupBook.fulfilled, (state) => {
        state.loading.litGroupBookEdit = false;
      })
      .addCase(uploadAndEditLitGroupBook.rejected, (state, action) => {
        state.loading.litGroupBookEdit = false;
        state.error = action.payload;
      })

      // ==============================
      // SET LITGROUP PROFILE IMAGE
      // ==============================
      .addCase(uploadAndSetLitGroupImage.pending, (state) => {
        state.loading.litGroupImage = true;
        state.error = null;
      })
      .addCase(uploadAndSetLitGroupImage.fulfilled, (state, action) => {
        state.loading.litGroupImage = false;
        if (state.activePrototype && state.activePrototype.publishedLitGroupUuid === action.payload.litGroupUuid) {
          state.activePrototype.litGroupImageUrl = action.payload.profileImageUrl;
        }
      })
      .addCase(uploadAndSetLitGroupImage.rejected, (state, action) => {
        state.loading.litGroupImage = false;
        state.error = action.payload;
      })

      // ==============================
      // UPDATE PROTOTYPE BOOK
      // ==============================
      .addCase(updatePrototypeBook.pending, (state) => {
        state.loading.bookUpdate = true;
        state.error = null;
      })
      .addCase(updatePrototypeBook.fulfilled, (state, action) => {
        state.loading.bookUpdate = false;
        const { book } = action.payload;
        const idx = state.activePrototypeBooks.findIndex(b => b.uuid === book.uuid);
        if (idx !== -1) state.activePrototypeBooks[idx] = { ...state.activePrototypeBooks[idx], ...book };
      })
      .addCase(updatePrototypeBook.rejected, (state, action) => {
        state.loading.bookUpdate = false;
        state.error = action.payload;
      })

      // ==============================
      // REMOVE PROTOTYPE BOOK
      // ==============================
      .addCase(removePrototypeBook.pending, (state) => {
        state.loading.bookRemove = true;
        state.error = null;
      })
      .addCase(removePrototypeBook.fulfilled, (state, action) => {
        state.loading.bookRemove = false;
        const { prototypeUuid, bookUuid } = action.payload;
        state.activePrototypeBooks = state.activePrototypeBooks.filter(b => b.uuid !== bookUuid);
        // Update bookCount in list
        const idx = state.prototypes.findIndex(p => p.uuid === prototypeUuid);
        if (idx !== -1 && state.prototypes[idx].bookCount !== undefined) {
          state.prototypes[idx].bookCount = Math.max(0, state.prototypes[idx].bookCount - 1);
        }
      })
      .addCase(removePrototypeBook.rejected, (state, action) => {
        state.loading.bookRemove = false;
        state.error = action.payload;
      })

      // ==============================
      // UPDATE AUTHOR IMAGE
      // ==============================
      .addCase(updateAuthorImage.pending, (state) => {
        state.loading.authorImage = true;
        state.error = null;
      })
      .addCase(updateAuthorImage.fulfilled, (state, action) => {
        state.loading.authorImage = false;
        const updated = action.payload.prototype;
        if (state.activePrototype?.uuid === updated.uuid) {
          state.activePrototype = { ...state.activePrototype, ...updated };
        }
        const idx = state.prototypes.findIndex(p => p.uuid === updated.uuid);
        if (idx !== -1) state.prototypes[idx] = { ...state.prototypes[idx], ...updated };
      })
      .addCase(updateAuthorImage.rejected, (state, action) => {
        state.loading.authorImage = false;
        state.error = action.payload;
      })

      // ==============================
      // UPDATE STATUS (including lockPrototype which delegates here)
      // ==============================
      .addCase(updatePrototypeStatus.pending, (state) => {
        state.loading.status = true;
        state.error = null;
      })
      .addCase(updatePrototypeStatus.fulfilled, (state, action) => {
        state.loading.status = false;
        const updated = action.payload.prototype;
        if (state.activePrototype?.uuid === updated.uuid) {
          state.activePrototype = { ...state.activePrototype, ...updated };
        }
        const idx = state.prototypes.findIndex(p => p.uuid === updated.uuid);
        if (idx !== -1) state.prototypes[idx] = { ...state.prototypes[idx], ...updated };
      })
      .addCase(updatePrototypeStatus.rejected, (state, action) => {
        state.loading.status = false;
        state.error = action.payload;
      });
  },
});

// ============================================================
// Actions
// ============================================================
export const {
  setWizardStep,
  clearActivePrototype,
  clearError,
  resetPrototypeState,
  setDeleteProgress,
  clearDeleteProgress,
} = litGroupPrototypeSlice.actions;

export const selectDeleteProgress = (state) => state.litGroupPrototype.deleteProgress;

// ============================================================
// Selectors
// ============================================================

/** All prototypes for the current user */
export const selectPrototypes = (state) => state.litGroupPrototype.prototypes;

/** Currently selected/editing prototype */
export const selectActivePrototype = (state) => state.litGroupPrototype.activePrototype;

/** Books for the active prototype (draft PrototypeBooks) */
export const selectActivePrototypeBooks = (state) => state.litGroupPrototype.activePrototypeBooks;

/** Live LitGroup books for a published prototype (the active series) */
export const selectActiveLiveBooks = (state) => state.litGroupPrototype.activeLiveBooks;

/** Loading state object */
export const selectPrototypeLoading = (state) => state.litGroupPrototype.loading;

/** Error state */
export const selectPrototypeError = (state) => state.litGroupPrototype.error;

/** Current wizard step */
export const selectWizardStep = (state) => state.litGroupPrototype.wizardStep;

/**
 * Readiness check: computes whether the prototype has all required assets.
 * Memoized — recomputes only when the prototype or its books change, so it returns
 * a stable reference and doesn't trigger unnecessary rerenders.
 * Returns { ready, checks: { details, hasPdfs, coverMatchRatio, authorImage } }
 */
export const selectPrototypeReadiness = createSelector(
  [selectActivePrototype, selectActivePrototypeBooks],
  (prototype, books) => {
    if (!prototype) {
      return {
        ready: false,
        checks: { details: false, hasPdfs: false, coverMatchRatio: '0/0', authorImage: false }
      };
    }

    const detailsComplete = !!(prototype.name && prototype.name.trim());
    const hasPdfs = books.length > 0;
    const booksWithCovers = books.filter(b => b.coverFileName);
    const coverMatchRatio = `${booksWithCovers.length}/${books.length}`;
    const allCoversMatched = books.length > 0 && booksWithCovers.length === books.length;
    const hasAuthorImage = !!prototype.authorImageFileName;

    const ready = detailsComplete && hasPdfs;

    return {
      ready,
      checks: {
        details: detailsComplete,
        hasPdfs,
        coverMatchRatio,
        allCoversMatched,
        authorImage: hasAuthorImage,
      }
    };
  }
);

/**
 * Cover match status: for each book, whether it has a matching cover. Memoized so the
 * derived array is stable between renders unless the books change.
 * Returns array of { bookUuid, fileName, coverFileName, hasMatch, baseName }
 */
export const selectCoverMatchStatus = createSelector(
  [selectActivePrototypeBooks],
  (books) => books.map(book => {
    const baseName = book.fileName
      ? book.fileName.replace(/\.pdf$/i, '').toLowerCase()
      : '';

    const coverBaseName = book.coverFileName
      ? book.coverFileName.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '').toLowerCase()
      : '';

    const hasMatch = !!book.coverFileName;
    const nameMatches = hasMatch && baseName === coverBaseName;

    return {
      bookUuid: book.uuid,
      fileName: book.fileName,
      coverFileName: book.coverFileName,
      hasMatch,
      nameMatches,
      baseName,
    };
  })
);

// ============================================================
// Export
// ============================================================
export default litGroupPrototypeSlice.reducer;
